import React, { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { DoorOpen, Trash2 } from "lucide-react";
import { useSecureChat } from "./SecureChatContext";
import { getUserByName } from "../utilities/ApiCalls";
import ProfileImage from "../../Componets/ProfileImage";
const api = require("../../api/secureMessageApi");
const Constant = require("../Constant");

const GroupInfoModal = ({ chat, onClose }) => {
  const { currentUser, updateChat } = useSecureChat();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [usersList, setUsersList] = useState(chat?.users || []);

  // Determine if current user is admin
  const adminId = chat?.groupAdmin?._id || chat?.groupAdmin;
  const isCurrentUserAdmin = adminId === currentUser?._id;

  // Proxy URL for images
  const proxiedUrl = useCallback((url) => {
    if (!url || url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com"))
      return url;
    return `${Constant.apiBasePath}proxy-image?url=${encodeURIComponent(url)}`;
  }, []);

  const searchUsers = useCallback(
    async (query) => {
      if (!query.trim() || query.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      try {
        setIsSearching(true);
        const resp = await getUserByName(encodeURIComponent(query));
        const foundUsers = resp?.data?.data || resp?.data || [];

        // Filter out users already in the group
        const filteredUsers = foundUsers.filter((user) => {
          const isAlreadyInGroup = usersList.some(
            (u) => (u._id || u) === user._id
          );
          return !isAlreadyInGroup;
        });

        setSearchResults(filteredUsers);
      } catch (err) {
        console.error("Error searching users:", err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [usersList]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        searchUsers(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchUsers]);

  const handleAddUser = async (userToAdd) => {
    try {
      setLoading(true);
      setError(null);

      let token = localStorage.getItem("loginDetails");
      const response = await fetch(
        `${Constant.apiBasePath}secure-message/group/add-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          body: JSON.stringify({
            groupId: chat._id,
            userIdToAdd: userToAdd._id,
          }),
        }
      );

      const data = await response.json();

      if (data.success || response.ok) {
        // Update local list
        const newUserList = [...usersList, userToAdd];
        setUsersList(newUserList);
        // Also update context if possible, or let socket/refetch handle it
        updateChat(chat._id, { users: newUserList });
        setSearchResults((prev) => prev.filter((u) => u._id !== userToAdd._id));
      } else {
        setError(data.message || "Failed to add user");
      }
    } catch (err) {
      console.error("Error adding user:", err);
      setError("Failed to add user");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userIdToRemove) => {
    if (!window.confirm("Are you sure you want to remove this user?")) return;

    try {
      setLoading(true);
      let token = localStorage.getItem("loginDetails");
      const response = await fetch(
        `${Constant.apiBasePath}secure-message/group/remove-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          body: JSON.stringify({
            groupId: chat._id,
            userIdToRemove: userIdToRemove,
          }),
        }
      );

      const data = await response.json();

      if (data.success || response.ok) {
        const newUserList = usersList.filter(
          (u) => (u._id || u) !== userIdToRemove
        );
        setUsersList(newUserList);
        updateChat(chat._id, { users: newUserList });
      } else {
        setError(data.message || "Failed to remove user");
      }
    } catch (err) {
      console.error("Error removing user:", err);
      setError("Failed to remove user");
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm("Are you sure you want to leave this group?")) return;

    try {
      setIsLeaving(true);
      let token = localStorage.getItem("loginDetails");
      // Using remove-user API for leaving as discussed
      const response = await fetch(
        `${Constant.apiBasePath}secure-message/group/remove-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          body: JSON.stringify({
            groupId: chat._id,
            userIdToRemove: currentUser._id,
          }),
        }
      );

      if (response.ok) {
        // Redirect or close modal, maybe refresh chats
        onClose();
        window.location.reload(); // Simple reload to refresh state completely
      } else {
        const data = await response.json();
        setError(data.message || "Failed to leave group");
      }
    } catch (err) {
      console.error("Error leaving group:", err);
      setError("Failed to leave group");
    } finally {
      setIsLeaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this group? This action cannot be undone."
      )
    )
      return;

    try {
      setIsLeaving(true);

      let token = localStorage.getItem("loginDetails");
      const response = await fetch(
        `${Constant.apiBasePath}secure-message/chat/${chat._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
        }
      );

      if (response.ok) {
        onClose();
        window.location.reload(); // Refresh to remove chat from list
      } else {
        const data = await response.json();
        setError(data.message || "Failed to delete group");
      }
    } catch (err) {
      console.error("Error deleting group:", err);
      setError("Failed to delete group");
    } finally {
      setIsLeaving(false);
    }
  };

  // renderInitial is no longer needed - handled by ProfileImage component

  return createPortal(
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContainer>
        <ModalHeader>
          <Title>Group Info</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalContent>
          <GroupProfile>
            <ProfileImage
              src={chat.displayPic}
              name={chat.displayName}
              size={80}
              alt={chat.displayName}
              proxiedUrl={proxiedUrl}
              avatarProps={{
                border: "2px solid var(--color-surface)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                fontSize: "32px",
              }}
              imgStyle={{
                border: "2px solid var(--color-surface)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            />
            <GroupName>{chat.displayName}</GroupName>
            <GroupDesc>{chat.groupDescription || "No description"}</GroupDesc>
          </GroupProfile>

          {isCurrentUserAdmin && (
            <Section>
              <SectionLabel>Add Participants</SectionLabel>
              <SearchInput
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchResults.length > 0 && (
                <SearchResults>
                  {searchResults.map((user) => (
                    <UserItem key={user._id}>
                      <UserInfo>
                        <ProfileImage
                          src={user.profilePic}
                          name={user.name}
                          size={36}
                          alt={user.name}
                          proxiedUrl={proxiedUrl}
                          avatarProps={{
                            border: "1px solid var(--color-border)",
                          }}
                          imgStyle={{
                            border: "1px solid var(--color-border)",
                          }}
                        />
                        <UserName>{user.name}</UserName>
                      </UserInfo>
                      <AddButton
                        disabled={loading}
                        onClick={() => handleAddUser(user)}
                      >
                        Add
                      </AddButton>
                    </UserItem>
                  ))}
                </SearchResults>
              )}
            </Section>
          )}

          <Section>
            <SectionLabel>Participants ({usersList.length})</SectionLabel>
            <UsersList>
              {usersList.map((user) => {
                // Handle case where user might be populated or just ID
                const uId = user._id || user;
                const uName = user.name || "User";
                const uPic = user.profilePic;
                const isAdmin = uId === adminId;

                return (
                  <UserItem key={uId}>
                    <UserInfo>
                      <ProfileImage
                        src={uPic}
                        name={uName}
                        size={36}
                        alt={uName}
                        proxiedUrl={proxiedUrl}
                        avatarProps={{
                          border: "1px solid var(--color-border)",
                        }}
                        imgStyle={{
                          border: "1px solid var(--color-border)",
                        }}
                      />
                      <UserName>{uName}</UserName>
                      {isAdmin && <AdminBadge>Admin</AdminBadge>}
                    </UserInfo>
                    {isCurrentUserAdmin && !isAdmin && (
                      <RemoveButton
                        onClick={() => handleRemoveUser(uId)}
                        title="Remove User"
                      >
                        ×
                      </RemoveButton>
                    )}
                  </UserItem>
                );
              })}
            </UsersList>
          </Section>

          {error && <ErrorText>{error}</ErrorText>}
        </ModalContent>

        <ModalFooter>
          {!isCurrentUserAdmin && (
            <LeaveButton onClick={handleLeaveGroup} disabled={isLeaving}>
              <DoorOpen size={18} /> Leave Group
            </LeaveButton>
          )}
          {isCurrentUserAdmin && (
            <DeleteButton onClick={handleDeleteGroup} disabled={isLeaving}>
              <Trash2 size={18} /> Delete Group
            </DeleteButton>
          )}
        </ModalFooter>
      </ModalContainer>
    </Overlay>,
    document.body
  );
};

export default GroupInfoModal;

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1200;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background-color: var(--color-surface);
  width: 100%;
  max-width: 500px;
  max-height: 85vh;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h3`
  margin: 0;
  color: var(--color-text);
  font-size: 18px;
  font-family: "Merriweather", serif;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text);
  font-size: 24px;
  cursor: pointer;
  opacity: 0.6;
  &:hover {
    opacity: 1;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const GroupProfile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  text-align: center;
`;

// GroupAvatar and DefaultGroupAvatar are now handled by ProfileImage component

const GroupName = styled.h2`
  color: var(--color-text);
  font-size: 20px;
  margin: 0 0 8px 0;
  font-family: "Merriweather", serif;
`;

const GroupDesc = styled.p`
  color: var(--color-text-secondary);
  font-size: 14px;
  margin: 0;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionLabel = styled.h4`
  color: var(--color-text-secondary);
  font-size: 12px;
  text-transform: uppercase;
  margin: 0 0 12px 0;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  background-color: var(--color-background);
  color: var(--color-text);
  outline: none;
  margin-bottom: 8px;

  &::placeholder {
    color: var(--color-text-secondary);
  }

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
  }
`;

const SearchResults = styled.div`
  background-color: var(--color-surface);
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
  border: 1px solid var(--color-border);
`;

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: transparent;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--color-background);
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// UserAvatar and DefaultUserAvatar are now handled by ProfileImage component

const UserName = styled.span`
  color: var(--color-text);
  font-size: 14px;
  font-weight: 500;
  font-family: "Merriweather", serif;
`;

const AdminBadge = styled.span`
  background-color: var(--color-primary);
  color: var(--color-surface);
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: bold;
  margin-left: 8px;
`;

const AddButton = styled.button`
  background-color: var(--color-text);
  color: var(--color-surface);
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: var(--color-primary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: var(--color-error);
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;

  &:hover {
    opacity: 0.8;
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  gap: 12px;
  background-color: var(--color-surface);
`;

const LeaveButton = styled.button`
  flex: 1;
  background-color: transparent;
  border: 1px solid var(--color-error);
  color: var(--color-error);
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--color-error);
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled.button`
  flex: 1;
  background-color: transparent;
  border: 1px solid var(--color-error);
  color: var(--color-error);
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--color-error);
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: var(--color-error);
  margin-top: 12px;
  font-size: 14px;
  text-align: center;
  padding: 8px;
  background-color: rgba(255, 107, 107, 0.1);
  border-radius: 4px;
`;
